import { EditOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { fmtEuro } from "../../../../utils/currency";
import type { LunchDraftItem } from "../lunchDraft";
import type { LunchProductView } from "../useLunchProducts";
import { lunchUnitOf } from "../useLunchProducts";

interface LunchSelectedRowProps {
  draftItem: LunchDraftItem;
  product: LunchProductView;
  onEdit: (itemId: number) => void;
  /** В bottom sheet-а — дребните размери на OrderSheet */
  inSheet?: boolean;
}

/**
 * Избран продукт в дясната колона на редактора — картата на „Преглед на
 * поръчката": молив + име + обедна цена; под тях размерът (кехлибар, ако е
 * сменен) и зачертаната основна цена. Клик → модалът за цена и размер.
 */
export const LunchSelectedRow = ({
  draftItem,
  product,
  onEdit,
  inSheet = false,
}: LunchSelectedRowProps) => {
  const { t, i18n } = useTranslation();
  const unit = lunchUnitOf(draftItem.unitAndSizeLangs, i18n.language, product.mainUnit);
  const lunchPrice = Number(draftItem.price);
  const priceChanged =
    product.mainPrice != null && Math.abs(lunchPrice - product.mainPrice) >= 0.005;
  // Размерите на „Преглед на поръчката" (OrderPanelContent): 15px на реда,
  // 13px на втория ред; в sheet-а — 13/11
  const lineText = inSheet ? "text-[13px]" : "text-[15px] leading-[19.5px]";
  const metaText = inSheet ? "text-[11px]" : "text-[13px] leading-[16.5px]";

  return (
    <button
      type="button"
      onClick={() => onEdit(draftItem.itemId)}
      className="flex w-full cursor-pointer flex-col gap-1.5 rounded-[10px] border border-pos-line3 bg-pos-card px-[11px] py-[9px] text-left transition-colors hover:border-pos-accent/40"
    >
      <span className="flex w-full items-center gap-2">
        <EditOutlined className={`${lineText} text-pos-accent-ink`} />
        <span className={`min-w-0 flex-1 truncate font-semibold text-pos-ink ${lineText}`}>
          {product.name}
        </span>
        <span className={`font-pos-mono font-bold text-pos-ink ${lineText}`}>
          {fmtEuro(lunchPrice)}
        </span>
      </span>
      <span className={`flex w-full items-center gap-2 ${inSheet ? "pl-[21px]" : "pl-[23px]"}`}>
        <span className={`font-pos-mono text-pos-soft2 ${metaText}`}>
          {unit.changed ? (
            <>
              {product.mainUnit && (
                <span className="text-pos-muted line-through">{product.mainUnit} </span>
              )}
              <span className="font-semibold text-pos-accent-ink">{unit.text}</span>
            </>
          ) : (
            (unit.text ?? "")
          )}
        </span>
        {/* Права черта грамаж | порции на ден („20/ден" или „∞" без ограничение) */}
        {unit.text && <span aria-hidden className="h-3 w-px shrink-0 bg-pos-muted/60" />}
        {draftItem.dailyLimit > 0 ? (
          <span className="rounded-[5px] bg-pos-marked px-1.5 py-px font-pos-mono text-[11px] font-semibold text-pos-accent-ink">
            {t("lunchMenu.dailyLimitShort", { count: draftItem.dailyLimit })}
          </span>
        ) : (
          <span
            title={t("lunchMenu.dailyLimitUnlimited")}
            // Знакът ∞ е дребен в mono шрифта — по-едър, без да вдига реда
            className="text-[17px] leading-none text-pos-muted"
          >
            ∞
          </span>
        )}
        <span className="flex-1" />
        {priceChanged && (
          <span className={`font-pos-mono text-pos-muted line-through ${metaText}`}>
            {fmtEuro(product.mainPrice)}
          </span>
        )}
      </span>
    </button>
  );
};
